// src/registrationForm.js
// Wording of the registration form (RegisterPage). Teachers edit it on
// Sign-ups → Form settings, which saves to config/registrationForm; anything
// not saved there falls back to DEFAULT_FORM below (copied from the original
// "AuthenTrix Registration" Google Form).
//
// The questions themselves and the four payment plan *types* are fixed here,
// because approving a sign-up (SignupsAdmin) maps them onto accounts/students.
import { useEffect, useState } from "react";
import { doc } from "firebase/firestore";
import { onSnapshot } from "./queryMonitor"; // timed reads (Billing → Performance)
import { db } from "./firebase";

export const FORM_CONFIG_DOC = "registrationForm"; // config/{FORM_CONFIG_DOC}
export const MAX_DANCERS = 10; // per sign-up; must match firestore.rules

// Fixed plan types and how Billing treats them. Labels/details are editable.
export const PLAN_TYPES = [
  { id: "monthly",    billingPeriod: "monthly",   billingNote: "Billed monthly" },
  { id: "quarterly",  billingPeriod: "quarterly", billingNote: "Billed quarterly" },
  { id: "annual",     billingPeriod: "annual",    billingNote: "Billed annually" },
  { id: "debitOrder", billingPeriod: "monthly",   billingNote: "Billed monthly (debit order)" },
];

export const DEFAULT_FORM = {
  // Off until a teacher opens it on Form settings (like the show's
  // "Bookings open to clients"); firestore.rules refuse sign-ups while closed.
  open: false,
  year: 2026,
  intro: "New and returning families. Takes about 5 minutes. You can register all your dancers in one go.",
  plans: {
    monthly:    { label: "Monthly",             detail: "12 instalments, paid in the first week of every month" },
    quarterly:  { label: "Quarterly",           detail: "4 instalments, paid in the first week of each quarter" },
    annual:     { label: "Yearly",              detail: "1 instalment, paid before 25 February 2026" },
    debitOrder: { label: "Debit order monthly", detail: "12 instalments, collected in the first week of every month" },
  },
  styles: [
    { label: "Modern",            detail: "3 years and over" },
    { label: "Acrobatics",        detail: "4 years and over" },
    { label: "Hip Hop",           detail: "5 years and over" },
    { label: "Tumbling",          detail: "7 years and over" },
    { label: "Technique",         detail: "6 years and over" },
    { label: "Competition Class", detail: "Compulsory for Acro and Modern soloists; recommended, but not compulsory, for duo dancers" },
  ],
  stylesNote:
    "Teacher Somari or Kaylee will send you a WhatsApp with available class times and open spots in each class. " +
    "You then have 24 hours to exclusively choose the classes in which you would like your spot to be secured. " +
    "After 24 hours, the available spots will be open to the next family who submitted their forms, but you can still answer once you have decided.",
  // Heading of the card the extra questions sit in, under each dancer.
  dancerQuestionsTitle: "A few more questions",
  // Extra Yes/No questions asked for each dancer, added on Form settings.
  // `id` stays fixed so answers line up even if the wording is edited later.
  dancerQuestions: [
    { id: "competitions", title: "Competitions", question: "Would this dancer like to take part in competitions this year?" },
  ],
  // Two separate permissions: using photos/videos privately to teach, and
  // posting them publicly. (Replaces the single photoConsentQuestion.)
  photoInstructionQuestion:
    "May AuthenTrix take photos and videos of this dancer during classes and rehearsals for instruction only — " +
    "for example to review technique and choreography with the dancer? These are not posted publicly.",
  photoPublicQuestion:
    "May AuthenTrix post photos and videos of this dancer from classes, workshops, performances and other events " +
    "on social media, the website and in advertising?",
  termsSummary:
    "Includes the R250 non-refundable registration fee per dancer, fees due by the 7th with a 10% late fee, 2 months' written notice to stop classes, and the risk and indemnity terms.",
  // AuthenTrix 2027 Terms and Conditions (22 Oct 2026). The indemnity and
  // medical consent are part of them (section 6), so there is no separate waiver.
  terms: "These terms apply to every dancer registered with AuthenTrix (Pty) Ltd for 2027. The studio opens on 13 January 2027 and closes on 8 December 2027. \"Parent\" means the parent, guardian or account holder who signs below.\n\n1. Fees and payment\n1.1 Fees are calculated over the full year and spread into equal monthly payments. They stay the same in months with holidays, and no credit or refund is given for missed classes.\n1.2 Monthly fees are due on or before the 7th of each month. Quarterly invoices must be paid in full by the 7th of the invoice month.\n1.3 A late fee of 10% of the outstanding amount is added to any payment not received by the 7th.\n1.4 If you cannot pay on time, contact the studio before the due date to make an arrangement.\n1.5 A dancer whose account is more than 60 days in arrears may not attend class until the account is paid in full.\n1.6 An account more than 30 days in arrears with no arrangement in place may be handed over for collection. The parent is liable for all legal and collection costs, on the attorney-and-own-client scale.\n1.7 Monthly fees do not include registration, entry fees for competitions, festivals or shows, costumes, or choreography of solos and duos/trios. Each group has a yearly costume cost that depends on its size, age and theme.\n\n2. Registration and commitment\n2.1 A non-refundable registration fee of R250 per dancer is payable before classes start in January 2027. A dancer is only placed on the class schedule once it is paid.\n2.2 Registration is a commitment for the full year.\n2.3 To stop classes, give the studio at least 2 months' written notice and pay the fees for that notice period.\n\n3. Classes and attendance\n3.1 There are no classes on public holidays, during school holidays, or during dance exams and competitions, unless the studio announces otherwise.\n3.2 Dancers must arrive on time and be collected promptly. AuthenTrix is not responsible for dancers outside class times.\n3.3 If a dancer cannot attend, inform the coach before the class.\n3.4 Missed classes cannot be caught up. Extra private lessons can be booked and are charged at the private lesson rate.\n3.5 Private lessons cancelled less than 24 hours ahead are charged at 50%, unless a doctor's note is provided. Lessons cancelled less than 2 hours ahead, or missed without notice, are charged in full.\n3.6 A coach may remove a dancer from class for misconduct or disrupting the class.\n\n4. Competitions, exams and shows\n4.1 AuthenTrix decides whether a dancer is ready to enter an exam, competition or show, and may withdraw a dancer who is not prepared because of poor attendance or performance.\n4.2 A dancer absent from more than 3 classes in a term may be removed from exams, competitions or festivals affected by these absences.\n4.3 A dancer whose fees are behind will not be entered into any exam or competition.\n4.4 Dancers and parents may not change a routine's choreography. If this happens, the dancer may not perform that routine under the studio's name.\n4.5 The coach chooses costumes, with age-appropriateness in mind, and has the final say. Costumes will not be changed for personal taste, but a dancer may choose not to take part in that number.\n4.6 All choreography belongs to the choreographer and may not be performed elsewhere or taught to others without their consent.\n\n5. Studio rules and communication\n5.1 All communication is sent by WhatsApp. Parents must have WhatsApp and keep up with studio messages. AuthenTrix is not responsible if a dancer misses out because a parent did not follow communicated arrangements.\n5.2 Please drop off and go, or wait inside a studio or in the parking lot. The foyer, staircase and common areas may not be used for waiting or play.\n5.3 Lost property is kept for one week and then donated to charity.\n5.4 Parents must pay for any damage to studio property caused by their dancer, family or friends.\n\n6. Risk, indemnity and medical consent\n6.1 Dance and acrobatics carry a risk of physical injury. The parent understands and accepts this risk on behalf of the dancer.\n6.2 Except where caused by gross negligence, the parent releases AuthenTrix, its affiliate studios, event sponsors, employees and coaches from liability for injury, illness or death, and for loss of or damage to personal property, during classes, rehearsals, workshops, performances, outings and tours.\n6.3 The parent indemnifies the same parties against claims, costs and judgments arising from acts or omissions of the parent or dancer that injure any person or damage property.\n6.4 Parents and dancers are responsible for their own personal belongings.\n6.5 If a dancer needs urgent medical treatment, the coaches may obtain appropriate medical help. The parent accepts responsibility for the cost.",
  waiver: "",
  // What families tick to agree (the terms' "Acceptance" section).
  acceptance: "I have read and understood these terms and agree to them on behalf of myself and my dancer.",
  successMessage:
    "Teacher Somari or Kaylee will WhatsApp you with available class times. Remember: you then have 24 hours to secure your spots.",
  termsVersion: "2027 (updated 22 Oct 2026)",
};

// Saved settings over the defaults. Plans merge per type so a missing one
// still shows; an empty styles list falls back to the defaults.
export function mergeForm(saved) {
  const s = saved || {};
  const plans = {};
  for (const t of PLAN_TYPES) plans[t.id] = { ...DEFAULT_FORM.plans[t.id], ...(s.plans?.[t.id] || {}) };
  return {
    ...DEFAULT_FORM,
    ...s,
    plans,
    styles: Array.isArray(s.styles) && s.styles.length ? s.styles : DEFAULT_FORM.styles,
    // A saved empty list means "no extra questions", so only fall back when never saved.
    dancerQuestions: Array.isArray(s.dancerQuestions) ? s.dancerQuestions : DEFAULT_FORM.dancerQuestions,
  };
}

// Live form wording. `form` is null until the first read finishes.
export function useRegistrationForm() {
  const [form, setForm] = useState(null);
  useEffect(() => onSnapshot(
    doc(db, "config", FORM_CONFIG_DOC),
    (snap) => setForm(mergeForm(snap.exists() ? snap.data() : null)),
    () => setForm(mergeForm(null))
  ), []);
  return form;
}

export const planType = (id) => PLAN_TYPES.find((t) => t.id === id);

// South African ID numbers start with YYMMDD. Returns "YYYY-MM-DD" or "".
export function dobFromSaId(idNumber) {
  const digits = (idNumber || "").replace(/\s/g, "");
  if (!/^\d{13}$/.test(digits)) return "";
  const yy = Number(digits.slice(0, 2)), mm = Number(digits.slice(2, 4)), dd = Number(digits.slice(4, 6));
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return "";
  const thisYY = new Date().getFullYear() % 100;
  const year = (yy > thisYY ? 1900 : 2000) + yy;
  return `${year}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

// ── "Already signed up" look-ups ────────────────────────────────────────────
// Kept up to date by the onRegistrationWritten Cloud Function, because the
// sign-ups themselves are teacher-only. Clients can fetch these by exact id
// but not list them. Name keys and masking must match functions/index.js.
export const STATUS_COLLECTION = "registrationStatus"; // doc id = lowercase email
export const NAMES_COLLECTION = "registrationNames";   // doc id = registrationNameKey()

export function registrationNameKey(prefix, name, surname) {
  const letters = `${name || ""}${surname || ""}`
    .normalize("NFD") // é → e + accent mark, which the letters-only filter drops
    .toLowerCase().replace(/[^a-z]/g, "");
  return letters ? `${prefix}_${letters}` : null;
}

// This year's sign-up for an email ({ dancers, parentName, … }), null when
// there isn't one, undefined while loading.
export function useRegistrationStatus(email, year) {
  const [status, setStatus] = useState(undefined);
  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    if (!e) { setStatus(null); return undefined; }
    setStatus(undefined);
    return onSnapshot(
      doc(db, STATUS_COLLECTION, e),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setStatus(data && (!year || !data.year || String(data.year) === String(year)) ? data : null);
      },
      () => setStatus(null)
    );
  }, [email, year]);
  return status;
}

// "Jane", "Jane and Jack", "Jane, Jack and Mia"
export function listNames(names) {
  const n = (names || []).filter(Boolean);
  return n.length <= 1 ? n.join("") : `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
}

// "jane@gmail.com" → "j•••@gmail.com"
export function maskEmail(email) {
  const [user, domain] = String(email || "").split("@");
  if (!domain) return "";
  return `${user.slice(0, 1)}•••@${domain}`;
}

// ── Field checks (RegisterPage) ─────────────────────────────────────────────
// Each returns an error message, or "" when the value is fine.

// South African cellphone: 10 digits starting with 0, e.g. 082 123 4567.
export function phoneError(phone) {
  const digits = (phone || "").replace(/[\s\-()]/g, "");
  if (!digits) return "Required";
  if (/^\+?27\d{9}$/.test(digits)) return `Start with 0 instead of +27, e.g. 0${digits.slice(-9, -7)} ${digits.slice(-7, -4)} ${digits.slice(-4)}`;
  if (!/^\d+$/.test(digits)) return "Use digits only, e.g. 082 123 4567";
  if (!digits.startsWith("0")) return "Start with 0, e.g. 082 123 4567";
  if (digits.length !== 10) return `A cellphone number has 10 digits — this has ${digits.length}`;
  return "";
}

// Luhn checksum — the last digit of an SA ID number.
function luhnValid(digits) {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}

// An SA ID number (13 digits: birth date, then a checksum) or, for dancers
// without one, a passport number (letters and digits).
export function idNumberError(idNumber) {
  const v = (idNumber || "").replace(/\s/g, "");
  if (!v) return "Required";
  if (/^\d+$/.test(v)) {
    if (v.length !== 13) return `An SA ID number has 13 digits — this has ${v.length}`;
    if (!dobFromSaId(v)) return "The first 6 digits should be the date of birth (YYMMDD)";
    if (new Date(`${dobFromSaId(v)}T12:00:00`) > new Date()) return "The date of birth in this ID number is in the future";
    if (!luhnValid(v)) return "This isn't a valid SA ID number — please check the digits";
    return "";
  }
  if (!/^[A-Za-z0-9]{6,20}$/.test(v)) return "Enter a 13-digit SA ID number, or a passport number (letters and digits only)";
  return "";
}

// Domains people commonly mistype, and what they meant.
const EMAIL_TYPOS = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gamil.com": "gmail.com", "gmail.co": "gmail.com",
  "gmail.con": "gmail.com", "gmail.cm": "gmail.com", "gmaill.com": "gmail.com", "gnail.com": "gmail.com",
  "hotmial.com": "hotmail.com", "hotmai.com": "hotmail.com", "hotmail.con": "hotmail.com",
  "outlook.con": "outlook.com", "outlok.com": "outlook.com",
  "yahoo.con": "yahoo.com", "yaho.com": "yahoo.com",
  "icloud.con": "icloud.com", "iclod.com": "icloud.com",
};

export function emailError(email) {
  const v = (email || "").trim().toLowerCase();
  if (!v) return "Required";
  if (/\s/.test(v)) return "An email address can't contain spaces";
  const m = /^([^@]+)@([^@]+)$/.exec(v);
  if (!m) return "Enter a valid email address, e.g. jane@gmail.com";
  const [, user, domain] = m;
  if (!/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(user) || user.startsWith(".") || user.endsWith(".") || user.includes("..")) {
    return "Enter a valid email address, e.g. jane@gmail.com";
  }
  if (!/^([a-z0-9-]+\.)+[a-z]{2,}$/.test(domain) || domain.split(".").some((part) => !part || part.startsWith("-") || part.endsWith("-"))) {
    return "Check the part after the @ — e.g. gmail.com";
  }
  if (EMAIL_TYPOS[domain]) return `Did you mean ${user}@${EMAIL_TYPOS[domain]}?`;
  return "";
}
